/**
 * JWT token creation and verification using HMAC-SHA256.
 *
 * Shared between the backend (issues tokens) and the sandbox-worker
 * (verifies tokens). Uses Web Crypto API — works in Cloudflare Workers,
 * Node 20+, and Deno.
 */

export interface JwtPayload {
  userId: string;
  githubId: number | null;
  githubLogin: string;
  iat: number;
  exp: number;
  /** Standard JWT audience claim (RFC 7519) — resource the token is bound to */
  aud?: string | string[];
  /** Standard JWT issuer claim (RFC 7519) */
  iss?: string;
  /** Standard JWT subject claim (RFC 7519) */
  sub?: string;
}

const ALGORITHM = 'HS256';

/** Default token lifetime: 30 days. */
const DEFAULT_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Fail-loud guard for signing/encryption secrets (L-3). A missing, non-string,
 * or blank secret must throw rather than silently sign with an empty key or
 * make verification return null. Present secrets are unaffected.
 */
export function assertSecret(secret: unknown, name = 'JWT secret'): asserts secret is string {
  if (typeof secret !== 'string' || secret.trim().length === 0) {
    throw new Error(`${name} is missing or empty`);
  }
}

function base64UrlEncode(data: ArrayBuffer | Uint8Array): string {
  const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function base64UrlDecode(str: string): Uint8Array {
  const padding = '='.repeat((4 - (str.length % 4)) % 4);
  const base64 = (str + padding).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function getHmacKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify'],
  );
}

/**
 * Create a signed JWT token.
 *
 * @param payload - User identity fields (userId, githubId, githubLogin)
 * @param secret  - HMAC signing secret (must match across backend and workers)
 * @param expiryMs - Token lifetime in ms (default: 30 days)
 */
export async function createJwt(
  payload: Omit<JwtPayload, 'iat' | 'exp'>,
  secret: string,
  expiryMs = DEFAULT_EXPIRY_MS,
): Promise<string> {
  assertSecret(secret);
  const now = Date.now();
  const fullPayload: JwtPayload = {
    ...payload,
    iat: now,
    exp: now + expiryMs,
  };

  const header = { alg: ALGORITHM, typ: 'JWT' };
  const enc = new TextEncoder();
  const encodedHeader = base64UrlEncode(enc.encode(JSON.stringify(header)));
  const encodedPayload = base64UrlEncode(enc.encode(JSON.stringify(fullPayload)));

  const key = await getHmacKey(secret);
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    enc.encode(`${encodedHeader}.${encodedPayload}`),
  );

  return `${encodedHeader}.${encodedPayload}.${base64UrlEncode(signature)}`;
}

/**
 * Verify a JWT token and return its payload.
 *
 * Returns `null` if the token is malformed, has an invalid signature,
 * or has expired.
 *
 * @param token  - The JWT string (header.payload.signature)
 * @param secret - HMAC signing secret
 */
export async function verifyJwt(
  token: string,
  secret: string,
): Promise<JwtPayload | null> {
  assertSecret(secret);
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  const [encodedHeader, encodedPayload, encodedSignature] = parts;

  const key = await getHmacKey(secret);
  const data = new TextEncoder().encode(`${encodedHeader}.${encodedPayload}`);
  const signature = base64UrlDecode(encodedSignature);

  const isValid = await crypto.subtle.verify(
    'HMAC',
    key,
    signature.buffer as ArrayBuffer,
    data,
  );
  if (!isValid) return null;

  const payload: JwtPayload = JSON.parse(
    new TextDecoder().decode(base64UrlDecode(encodedPayload)),
  );

  if (payload.exp < Date.now()) return null;

  return payload;
}
